import { useSPAContext } from "@/providers/spa-provider";
import { useDexDetails, useClaimDexReward } from "@/hooks/queries/use-dex";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { toast } from "@/components/ui/toast/toast-store";
import type { DexSlot } from "types/web-app";

const SlotEntry = ({ slot }: { slot: DexSlot }) => {
  const baseClass = "flex items-center gap-2 p-1 rounded";

  if (slot.status === "obtained") {
    return (
      <div className={`${baseClass} bg-pk-gray-2`}>
        <img
          src={`/assets/sprites/${slot.species_name}.png`}
          alt={slot.species_name}
          className="w-8 h-8 object-contain"
          style={{ imageRendering: "pixelated" }}
        />
        <span className="text-white text-xs capitalize">
          {slot.species_name.replace(/-/g, " ")}
          {slot.shiny_seen && " ✦"}
        </span>
      </div>
    );
  }

  if (slot.status === "reachable") {
    return (
      <div className={`${baseClass} bg-pk-gray-2/50 border border-pk-yellow-1/30`}>
        <div className="w-8 h-8 rounded bg-pk-gray-4/50 flex items-center justify-center">
          <span className="text-pk-yellow-1 text-xs">→</span>
        </div>
        <div className="flex flex-col">
          <span className="text-white/60 text-xs capitalize">
            {slot.species_name.replace(/-/g, " ")}
          </span>
          <span className="text-pk-yellow-1/70 text-[10px]">
            evolve: {slot.reachable_from?.map((s) => s.replace(/-/g, " ")).join(", ")}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${baseClass} bg-pk-gray-4/20`}>
      <div className="w-8 h-8 rounded bg-pk-gray-4/40 flex items-center justify-center">
        <span className="text-white/20 text-xs">?</span>
      </div>
      <span className="text-white/30 text-xs capitalize">
        {slot.species_name.replace(/-/g, " ")}
      </span>
    </div>
  );
};

export const DexSetDetailsView = () => {
  const { viewedDexSetId } = useSPAContext();
  const { data, isLoading, isError, refetch } = useDexDetails(viewedDexSetId ?? null);
  const claimMutation = useClaimDexReward();

  const handleClaim = async () => {
    if (!viewedDexSetId) return;
    try {
      await claimMutation.mutateAsync({ set_id: viewedDexSetId });
      toast.success("Reward claimed!");
    } catch {
      toast.error("Failed to claim reward.");
    }
  };

  if (isLoading) {
    return <div className="text-center text-white/50 py-8">Loading...</div>;
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-2 text-center text-white/50 py-8">
        <span>Failed to load set.</span>
        <SimpleButton variant="gray" onClick={() => refetch()}>Retry</SimpleButton>
      </div>
    );
  }

  return (
    <div className="w-full p-1 flex flex-col gap-3 h-full overflow-y-auto">
      <StandardFrame variant="purple">
        <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-black text-base">{data.display_name}</h2>
            <p className="text-white/60 text-xs">{data.obtained_count}/{data.total} species</p>
          </div>
          {data.claimable && (
            <SimpleButton
              variant="yellow"
              onClick={handleClaim}
              disabled={claimMutation.isPending}
            >
              {claimMutation.isPending ? "..." : "Claim Reward"}
            </SimpleButton>
          )}
          {data.claimed && (
            <span className="text-white/40 text-sm">Claimed</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-1">
          {data.slots.map((slot) => (
            <SlotEntry key={slot.species_name} slot={slot} />
          ))}
        </div>
        </div>
      </StandardFrame>
    </div>
  );
};
